import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useParams } from "react-router-dom";
import ActionMenu from "../../../../../components/app/ActionMenu";
import StatusChip from "../../../../../components/StatusChip";
import {
  CheckIcon,
  CrossIcon,
} from "../../../../../components/svg-icons/CheckCrossIcons";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import Text from "../../../../../components/Text";
import { downloadPdf } from "../../../../../hooks/use-download-pdf";
import { useQueryParams } from "../../../../../hooks/use-query-param";
import { useReduceDisclosure } from "../../../../../hooks/use-reduce-disclosure";
import { useArchiveState } from "../../../../../hooks/useArchiveState";
import { DOC_TYPE_MAPPER } from "../../../../../pages/app/provider/patient/patient-list/patient-chart-new/document/document-constant";
import { useEducationMaterialArchive } from "../../../../../queries/education-material/use-education-material-archive";
import { useEducationMaterialList } from "../../../../../queries/education-material/use-education-material-list";
import { NonNullableDate } from "../../../../../utils/date-time-utility";
import {
  applyDefaultColumnSettings,
  DEF_PAGINATION,
  DGT_Pagination,
  filterActionCol,
  getIdFromObj,
  getValueFromObj,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../utils/utils";
import RenderWithTooltip from "../../../../RenderWithTooltip";
import AllFilePreview from "../../../../ViewAllFile";
import DeleteModal from "../../../DeleteModal";
import { EducationMaterialFilterInitialValuesType } from "./EducationMaterialFilter";
import EducationShareWithDrawer from "./EducationShareWithDrawer";
import UpsertEducationDrawer from "./UpsertEducationDrawer";
import { getPublicRealm } from "../../../../../layout/app/patient-portal/public-view/dob-validation/useValidateDob";
import { GlobalTableHeightWrapper } from "../../../../GlobalTableHeightWrapper";

interface EducationMaterialTableProps {
  filter?: any;
  search?: string;
  isPublicView?: boolean;
  isPatientChartView?: boolean;
  shouldAction?: boolean;
  archive?: boolean;
  setFilter: (v: EducationMaterialFilterInitialValuesType) => void;
}

const EducationMaterialTable = ({
  filter,
  search,
  isPublicView = false,
  isPatientChartView = false,
  shouldAction = true,
  archive = false,
  setFilter: _setFilter,
}: EducationMaterialTableProps) => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const { mutateAsync: archiveMutation } = useEducationMaterialArchive();
  const { deleteDisc, archiveHandler } = useArchiveState(archiveMutation);
  const patientId = useQueryParams("patientId");
  const { patientId: patientID } = useParams() as { patientId: any };
  const xTENANTID = getPublicRealm();
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const {
    data: educationMaterialList,
    isLoading,
    isRefetching,
    refetch,
  } = useEducationMaterialList({
    pagination,
    gridSorting,
    specialityId: filter?.speciality ?? undefined,
    patientId: patientId || patientID || undefined,
    providerId: filter?.uploaded_by?.value ?? undefined,
    searchString: search,
    archive: archive,
    xTENANTID: isPublicView ? xTENANTID : getXtenantId(),
  });

  const [shareDisc, viewDisc, editDisc] = useReduceDisclosure(3);
  const isVisitNotePage = window.location.pathname.includes("/visit-note");

  const handleActionClick = async (v: string, row: any) => {
    if (v === "view") {
      viewDisc.open(row);
    } else if (v === "download") {
      const response = await fetch(row.document);
      const blob = await response.blob();
      downloadPdf(blob, `${row.name}.${row.documentFormat}`);
    } else if (v === "share") {
      shareDisc.open(row.uuid);
    } else if (v === "edit") {
      const id = getIdFromObj(row?.speciality);
      const providerId = getIdFromObj(row?.provider);
      const providerName = getValueFromObj(row?.provider);

      const transformedRow = {
        ...row,
        providerId: row.provider
          ? { value: providerId, label: providerName }
          : undefined,
        specialityId: id,
        documentFormat: row.documentFormat,
        isEditFile: true,
        file: row.document,
      };

      editDisc.open(transformedRow);
    } else {
      deleteDisc.open(row.uuid);
    }
  };

  const COLUMNS = applyDefaultColumnSettings(
    [
      // {
      //   field: "id",
      //   headerName: "Sr.No",
      //   maxWidth: 70,
      //   renderCell: (params) =>
      //     params.api.getAllRowIds().indexOf(params.id) + 1,
      // },
      {
        field: "name",
        headerName: "Title",
        flex: 0.8,
        minWidth: 150,
        renderCell: ({ value, row }) => (
          <Text
            sx={{ cursor: "pointer" }}
            onClick={() => viewDisc.open(row)}
            color="primary"
          >
            <RenderWithTooltip value={value} />
          </Text>
        ),
      },
      {
        field: "speciality",
        headerName: "Specialty",
        flex: 0.5,
        minWidth: 120,
        renderCell: ({ value }) => {
          const label = getValueFromObj(value);
          return label ? (
            <StatusChip label={label} variant="neutral" />
          ) : (
            <Text isTabletext>-</Text>
          );
        },
      },
      {
        field: "description",
        headerName: "Description",
        flex: 0.8,
        minWidth: 150,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "condition",
        headerName: "Condition",
        flex: 0.6,
        minWidth: 130,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "documentFormat",
        headerName: "File Type",
        flex: 0.3,
        minWidth: 80,
        renderCell: ({ value }) => replaceUnderScoreAddSpace(value) || "-",
      },
      // {
      //   field: "provider",
      //   headerName: "",
      //   flex: 0.3,
      //   minWidth: 100,
      //   renderCell: ({ value }) => <Text>{getValueFromObj(value)}</Text>,
      // },
      {
        field: "provider",
        headerName: "Uploaded By",
        flex: 0.4,
        minWidth: 120,
        renderCell: ({ value }) => (
          <Text isTabletext>{getValueFromObj(value)}</Text>
        ),
      },
      {
        field: "created",
        headerName: "Uploaded Date",
        flex: 0.4,
        minWidth: 120,
        renderCell: ({ row }) => (
          <Text isTabletext>{NonNullableDate(row.uploadedAt)}</Text>
        ),
      },
      {
        field: "shared",
        headerName: "Shared with Patient",
        flex: 0.4,
        minWidth: 140,
        renderCell: ({ value }) =>
          value ? (
            <CheckIcon sx={{ fontSize: 14 }} />
          ) : (
            <CrossIcon sx={{ fontSize: 14 }} />
          ),
      },
      {
        field: "Action",
        headerName: "Action",
        width: 90,
        sortable: false,
        renderCell: ({ row }: any) => (
          <ActionMenu
            options={[
              {
                value: "view",
                label: "View",
              },
              {
                value: "download",
                label: "Download",
              },
              {
                value: "share",
                label: "Share",
                isDisabled: row?.archive,
                hoverMsg:
                  "You cannot Perform Operation on Archived Education Material",
              },
              {
                value: "edit",
                label: "Edit",
                isDisabled: row?.archive,
                hoverMsg:
                  "You cannot Perform Operation on Archived Education Material",
              },
              {
                value: "archive",
                label: row?.archive ? "Unarchive" : "Archive",
              },
            ].filter((it) => (isPublicView ? it.value === "view" : true))}
            onActionClick={(v) => handleActionClick(v, row)}
          />
        ),
      },
    ],
    ["created"]
  );

  const dynamicColumns = COLUMNS.filter((column) =>
    !shouldAction ? column.field !== "action" : column
  );

  return (
    <>
      {shareDisc.opened && (
        <EducationShareWithDrawer disclosureHost={shareDisc} />
      )}

      {editDisc.opened && <UpsertEducationDrawer disclosureHost={editDisc} />}

      {viewDisc.opened && (
        <AllFilePreview
          disclosureHost={viewDisc}
          file={viewDisc?.data?.document}
          fileType={DOC_TYPE_MAPPER[viewDisc.data?.documentFormat] ?? "other"}
          title="Preview Education Material"
          w="48vw"
        />
      )}

      <DeleteModal
        w="380px"
        open={deleteDisc.opened}
        onClose={deleteDisc.close}
        id={deleteDisc.deleteId}
        onConfirmation={(id) =>
          archiveHandler(!archive, id).then((res) => {
            refetch();
            return res;
          })
        }
        content={`You want to ${
          archive ? "unarchive" : "archive"
        } this Education Material`}
      />
      {/* <ActiveFilters filters={filter} setFilters={setFilter as any} /> */}
      <GlobalTableHeightWrapper marginTop={isPatientChartView ? 0 : 1}>
        <CustomDataGridV2
          loading={isLoading || isRefetching}
          rows={educationMaterialList?.data?.content ?? []}
          rowCount={educationMaterialList?.data?.page?.totalElements ?? 0}
          getRowId={(row) => row.uuid}
          columns={filterActionCol(dynamicColumns, isVisitNotePage)}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          disableRowSelectionOnClick
          onSortModelChange={setGridSorting}
          sortModel={gridSorting}
        />
      </GlobalTableHeightWrapper>
    </>
  );
};

export default EducationMaterialTable;
